import {Component, DestroyRef, inject} from '@angular/core';
import {BaseModalComponent} from "../base-modal/base-modal.component";
import {MAT_DIALOG_DATA, MatDialog} from "@angular/material/dialog";
import {SettingsTableDialogData} from "./types/settings-table.types";
import {MatButton, MatIconButton, MatMiniFabButton} from "@angular/material/button";
import {MatFormField} from "@angular/material/input";
import {MatLabel} from "@angular/material/form-field";
import {MatOption, MatSelect, MatSelectTrigger} from "@angular/material/select";
import {FormsModule} from "@angular/forms";
import {ColumnParamsStateService} from "../../../services/table-services/column-params-state.service";
import {RecordTableColumnParams} from "../../../types/records-table.types";
import {MatCheckbox} from "@angular/material/checkbox";
import {MatDivider} from "@angular/material/divider";
import {MatIcon} from "@angular/material/icon";
import {SettingsTableModalService} from "./services/settings-table-modal.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {UserPreferencesService} from "../../../services/user-preferences.service";
import {settingsTableModalName} from "./configs/settings-table-modal-config";
import {ProfileColumnModalComponent} from "../profile-column-modal/profile-column-modal.component";

@Component({
  selector: 'app-settings-table-modal',
  imports: [
    BaseModalComponent,
    MatButton,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    FormsModule,
    MatCheckbox,
    MatDivider,
    MatIconButton,
    MatIcon,
    MatMiniFabButton,
    MatSelectTrigger
  ],
  templateUrl: './settings-table-modal.component.html',
  styleUrl: './settings-table-modal.component.scss'
})
export class SettingsTableModalComponent extends BaseModalComponent {
  private settingsTableDialogData = inject<SettingsTableDialogData>(MAT_DIALOG_DATA);
  private columnParamsStateService: ColumnParamsStateService = inject(ColumnParamsStateService);
  private settingsTableModalService: SettingsTableModalService = inject(SettingsTableModalService);

  public recordTableColumnParams: Array<RecordTableColumnParams> = [];
  public selectedColumns: Array<RecordTableColumnParams> = [];
  public selectedProfile: string = '';
  public columnProfiles: Array<string> = [];
  private userPreferencesService: UserPreferencesService = inject(UserPreferencesService);
  private destroyRef = inject(DestroyRef);
  private dialog: MatDialog = inject(MatDialog);

  public override ngOnInit() {
    super.ngOnInit();
    this.modalStatesService.openModal(settingsTableModalName);
    this.initComponentData();
    this.initColumnProfilesSubscription();
  }

  public override ngOnDestroy() {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(settingsTableModalName);
  }

  public handleChangeProfile(): void {
    this.settingsTableModalService.updateActiveProfile(this.selectedProfile);
    const hiddenColumns = this.settingsTableModalService.getHiddenColumnsByActive();

    this.columnParamsStateService.updateActiveProfile({
      key: this.settingsTableDialogData.parentUrl,
      columnKeys: hiddenColumns,
      profileName: this.selectedProfile
    });

    this.selectedColumns = this.columnParamsStateService.getColumnParamsByKey(this.settingsTableDialogData.parentUrl) || [];
    this.closeDialog('cancel');
  }

  public async handleEditProfile(event: MouseEvent, name: string): Promise<void> {
    event.stopPropagation();
    await this.modalService.openProfileColumnModal({
      currentProfileName: name,
      columnProfiles: this.columnProfiles
    });
  }

  public handleDeleteProfile(event: MouseEvent, name: string): void {
    event.stopPropagation();
    if (name === this.selectedProfile) {
      this.errorService.showErrorMessage('Нельзя удалить выбранный профиль!');
      return;
    }

    this.columnProfiles = this.settingsTableModalService.deleteColumnProfile(name)?.map(i => i.profileName) || [];
  }

  public async handleAddProfile(): Promise<void> {
    if (this.columnProfiles.length === 5) {
      this.errorService.showErrorMessage('Нельзя создать больше 5 профилей!');
      return;
    }

    this.dialog.open(ProfileColumnModalComponent, {
      data: this.columnProfiles
    }).afterClosed().subscribe(result => {
      if (!result || typeof result !== 'object') { return; }

      this.selectedProfile = result['newProfileName'];
      this.columnParamsStateService.updateActiveProfile({
        key: this.settingsTableDialogData.parentUrl,
        columnKeys: [],
        profileName: this.selectedProfile
      });

      this.selectedColumns = this.columnParamsStateService.getColumnParamsByKey(this.settingsTableDialogData.parentUrl) || [];
    });
  }

  public isAllSelected(): boolean {
    const selectableColumns = this.recordTableColumnParams.filter(col => !col.sticky);
    const selectedSelectable = this.selectedColumns.filter(col => !col.sticky);
    return selectedSelectable.length === selectableColumns.length;
  }

  public toggleSelectAll(): void {
    const selectableColumns = this.recordTableColumnParams.filter(col => !col.sticky);
    const stickyColumns = this.selectedColumns.filter(col => col.sticky);

    this.selectedColumns = this.isAllSelected()
      ? stickyColumns
      : [...stickyColumns, ...selectableColumns];
  }

  public handleSaveSettings(): void {
    if (!this.selectedProfile) {
      this.errorService.showErrorMessage('Выберите профиль или создайте новый!');
      return;
    }

    const initialVisibleKeys = new Set(
      (this.columnParamsStateService.getColumnParamsByKey(this.settingsTableDialogData.parentUrl) || [])
        .map(col => col.key)
    );
    const newVisibleKeys = new Set(this.selectedColumns.map(col => col.key));
    const columnsToToggle = this.recordTableColumnParams
      .filter(column => initialVisibleKeys.has(column.key) !== newVisibleKeys.has(column.key))
      .map(column => column.key);

    columnsToToggle.length &&
      this.columnParamsStateService.updateHiddenColumn({
        key: this.settingsTableDialogData.parentUrl,
        columnKeys: columnsToToggle,
        profileName: this.selectedProfile
      });

    this.closeDialog('cancel');
  }

  private initComponentData(): void {
    this.recordTableColumnParams = this.columnParamsStateService.getAllColumnParamsByKey(this.settingsTableDialogData.parentUrl) || [];
    this.selectedColumns = this.columnParamsStateService.getColumnParamsByKey(this.settingsTableDialogData.parentUrl) || [];
    this.selectedProfile = this.settingsTableModalService.getSelectedProfile();
    this.columnProfiles = this.settingsTableModalService.getProfileSelectData();
  }

  private initColumnProfilesSubscription(): void {
    this.userPreferencesService.userPreferences$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.columnProfiles = this.settingsTableModalService.getProfileSelectData();
      });
  }
}
